import { useEffect, useMemo, useState } from 'react';
import { Search, Mail, Phone, RefreshCw, Filter, ExternalLink } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { adminListEnquiries } from '../../../lib/adminApi';
import { SectionHeader, Card } from './_ui';

function fmt(dt) {
  if (!dt) return '';
  try {
    const d = new Date(dt);
    return d.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dt;
  }
}

export default function EnquiriesSection() {
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState({ course: 'all', dateFrom: '', dateTo: '', q: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = async () => {
    setError('');
    setLoading(true);
    try {
      const params = { ...filters };
      // Strip empties so backend defaults kick in.
      Object.keys(params).forEach((k) => {
        if (!params[k] || (k === 'course' && params[k] === 'all')) delete params[k];
      });
      const data = await adminListEnquiries(params);
      setItems(data.items || []);
      setCourses(data.courses || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load enquiries.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (e) => {
    e?.preventDefault?.();
    refresh();
  };

  const csvUrl = useMemo(() => {
    // simple client-side CSV for one-off exports
    const header = ['Received', 'Name', 'Email', 'Phone', 'Course', 'Year', 'Board'];
    const rows = items.map((r) => [
      r.receivedAt, r.name, r.email, r.phone || '', r.course, r.yearGroup, r.examBoard,
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((v) => `"${String(v || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    return `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  }, [items]);

  return (
    <div className="max-w-6xl">
      <SectionHeader
        title="Enquiries"
        description="Every contact-form submission - newest first. Reply straight from your inbox; the message is threaded to the parent."
        actions={
          <>
            <button
              type="button"
              onClick={refresh}
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-ink/15 px-3 py-1.5 text-sm text-brand-ink hover:bg-brand-ink/5"
              data-testid="enquiries-refresh"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <a
              href={csvUrl}
              download="enquiries.csv"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-sun text-brand-ink hover:brightness-95 px-3 py-1.5 text-sm font-semibold"
            >
              <ExternalLink className="w-4 h-4" /> Export CSV
            </a>
          </>
        }
      />

      <Card className="mb-4">
        <form onSubmit={applyFilters} className="grid md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-brand-ink/70 flex items-center gap-1">
              <Filter className="w-3 h-3" /> Course
            </label>
            <Select
              value={filters.course}
              onValueChange={(v) => setFilters({ ...filters, course: v })}
            >
              <SelectTrigger className="mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All courses</SelectItem>
                {courses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-brand-ink/70">From</label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-brand-ink/70">To</label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-brand-ink/70 flex items-center gap-1">
              <Search className="w-3 h-3" /> Search name / email / phone
            </label>
            <div className="flex gap-2 mt-1">
              <Input
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="e.g. Sarah"
                className="flex-1"
              />
              <button
                type="submit"
                className="rounded-full bg-brand-ink text-brand-cream hover:bg-brand-teal px-4 text-sm"
              >
                Apply
              </button>
            </div>
          </div>
        </form>
      </Card>

      {loading ? (
        <p className="text-brand-ink/60">Loading enquiries...</p>
      ) : error ? (
        <p className="text-red-600" role="alert">{error}</p>
      ) : items.length === 0 ? (
        <Card>
          <p className="text-brand-ink/70">No enquiries match your filters.</p>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-brand-ink/10 bg-brand-cream/40">
                  <th className="p-3 font-semibold">Received</th>
                  <th className="p-3 font-semibold">Name</th>
                  <th className="p-3 font-semibold">Contact</th>
                  <th className="p-3 font-semibold">Course</th>
                  <th className="p-3 font-semibold">Year</th>
                  <th className="p-3 font-semibold">Board</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-brand-ink/5 last:border-0 align-top"
                    data-testid={`enquiry-row-${r.id}`}
                  >
                    <td className="p-3 whitespace-nowrap text-brand-ink/70">{fmt(r.receivedAt)}</td>
                    <td className="p-3 font-medium text-brand-ink">{r.name}</td>
                    <td className="p-3">
                      <div className="flex flex-col gap-0.5">
                        {r.email && (
                          <a
                            href={`mailto:${r.email}`}
                            className="inline-flex items-center gap-1 text-brand-teal hover:underline"
                          >
                            <Mail className="w-3.5 h-3.5" /> {r.email}
                          </a>
                        )}
                        {r.phone && (
                          <span className="inline-flex items-center gap-1 text-brand-ink/70">
                            <Phone className="w-3.5 h-3.5" /> {r.phone}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-brand-ink">{r.course}</td>
                    <td className="p-3 text-brand-ink/80">{r.yearGroup}</td>
                    <td className="p-3 text-brand-ink/80">{r.examBoard}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 text-xs text-brand-ink/50 border-t border-brand-ink/10">
            Showing {items.length} enquiry{items.length === 1 ? '' : 'ies'}.
          </div>
        </Card>
      )}
    </div>
  );
}
